/**
 * ProjectsListWidget — date-grouped list of upcoming projects, grouped by
 * due_date. statusId is included on each item so the FE can match against
 * project statuses (public.status WHERE type='project') for the small
 * accent strip — but the bootstrap catalog doesn't currently hydrate
 * project statuses, so we leave the strip neutral for v1.
 */

import * as React from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import { DateGroupedList, RefreshButton, ViewAllLink, WidgetCard } from "components/dashboard";
import { useProjectsList, formatQueryError } from "../hooks";
import type { LayoutItem, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

export function ProjectsListWidget({ widget, layout }: Props) {
  const { data, isLoading, error } = useProjectsList();
  const history = useHistory();
  const queryClient = useQueryClient();

  const groups = (data?.groups ?? []).map((g) => ({
    date: g.date,
    count: g.count,
    onViewMore: () => history.push("/projects"),
    items: g.items.map((item) => ({
      id: item.id,
      label: item.label,
      subLabel: item.subLabel,
      time: item.time,
      onClick: () => history.push(`/projects/${item.id}`),
    })),
  }));

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "projects-list"])}
        />
      }
      footer={
        <ViewAllLink label="View all projects" onClick={() => history.push("/projects")} />
      }
    >
      <DateGroupedList
        groups={groups}
        columnHeaders={{ left: "Due Date", right: "Projects Count" }}
        emptyMessage="No upcoming projects"
      />
    </WidgetCard>
  );
}
